/**
 * BorrowerRep component - Manages representative and shareholder/director information
 */

import { FC, useEffect, useState } from "react";
import { Button, color } from "@funded-here-interface/common";
import Line from "../../assets/LineHorizontal.png";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@funded-here-interface/investor-onboarding/src/store";
import { ShareholderDirector } from "../../features/merchant/rep";
import { useStyles } from "./BorrowerRep.styles";
import { toast } from "react-toastify";
import AuthRepInfo from "../../components/AuthRepInfo/AuthRepInfo";
import {
  CompanyRole,
  ShareholderDirectorField,
} from "@funded-here-interface/common/src/constant/enum";
import ShareholdersDirectors from "../../components/ShareholdersDirectors/ShareholdersDirectors";
import User from "./Components/User/User";
import Auth from "../../components/Auth/Auth";
import useGetOrgDraft from "../../hooks/useGetOrgDraft";
import useSaveOrgDraft from "../../hooks/useSaveOrgDraft";
import { RepFormData } from "./types";
import { INITIAL_ERRORS } from "./constants";
import {
  deriveCheckboxState,
  hydrateRepDataToRedux,
  extractDirectorEmails,
  wasAuthCheckboxChecked,
  wasDirectorCheckboxChecked,
} from "./utils/repFormUtils";
import { TabComponentProps } from "../SalesOnboarding/types";

const BorrowerRep: FC<TabComponentProps> = ({
  isEditable,
  merchantId,
  merchantEmail,
  onDirtyChange,
  onSaveDraft,
  onResetForm,
}) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const userEmail = merchantEmail || "";
  const userId = merchantId.toString();
  const { token } = useSelector((state: RootState) => state.auth);

  const getOrgDraftMutation = useGetOrgDraft();
  const saveOrgDraftMutation = useSaveOrgDraft();

  const [initPage, setInitPage] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isDirty, setIsDirty] = useState(false);
  const [shouldMarkDirty, setShouldMarkDirty] = useState(true);
  const [initialAuthChecked, setInitialAuthChecked] = useState(false);
  const [initialDirectorChecked, setInitialDirectorChecked] = useState(false);
  const [initialDraftData, setInitialDraftData] = useState<RepFormData | null>(
    null
  );

  const fetchDraft = async (userId: number) => {
    try {
      const params = {
        userId: userId,
        token: token,
      };
      const result = await getOrgDraftMutation.mutateAsync(params);
      return result;
    } catch (error) {
      console.error("Error fetching draft:", error);
    }
  };

  const {
    repFirstName: repFirstNameRedux,
    repLastName: repLastNameRedux,
    userFirstName: userFirstNameRedux,
    userLastName: userLastNameRedux,
    repTitle: repTitleRedux,
    merchantTitle: merchantTitleRedux,
    shareholdersDirectors: shareholdersDirectorsRedux,
    repEmail: repEmailRedux,
  } = useSelector((state: RootState) => state.Borrep);

  const [authChecked, setAuthChecked] = useState(false);
  const [directorChecked, setDirectorChecked] = useState(false);
  const [representativeFirstName, setRepresentativeFirstName] = useState(
    repFirstNameRedux || ""
  );
  const [representativeLastName, setRepresentativeLastName] = useState(
    repLastNameRedux || ""
  );
  const [representativeTitle, setRepresentativeTitle] = useState(
    repTitleRedux || ""
  );
  const [merchantTitle, setMerchantTitle] = useState(merchantTitleRedux || "");
  const [merchantFirstName, setMerchantFirstName] = useState(
    userFirstNameRedux || ""
  );
  const [repEmail, setRepEmail] = useState(repEmailRedux || "");
  const [merchantLastName, setMerchantLastName] = useState(
    userLastNameRedux || ""
  );
  const [numOfShareholdersDirectors, setNumOfShareholdersDirectors] = useState(
    shareholdersDirectorsRedux?.length || 1
  );
  const [shareholdersDirectors, setShareholdersDirectors] = useState<
    ShareholderDirector[]
  >(shareholdersDirectorsRedux || []);

  const [error, setError] = useState(INITIAL_ERRORS);

  const handleInputChange = (
    setter: React.Dispatch<React.SetStateAction<any>>,
    value: any
  ) => {
    setter((prevValue: any) => {
      if (prevValue !== value && shouldMarkDirty) {
        setIsDirty(true);
        onDirtyChange(true);
      }
      return value;
    });
  };

  const updateShareholderDirectorValue = (
    index: number,
    field: ShareholderDirectorField,
    value: string
  ) => {
    if (shareholdersDirectors[index]?.email === repEmail && directorChecked) {
      if (field === ShareholderDirectorField.EMAIL) {
        handleInputChange(setRepEmail, value);
      }
      if (field === ShareholderDirectorField.FIRSTNAME) {
        handleInputChange(setRepresentativeFirstName, value);
      }
      if (field === ShareholderDirectorField.LASTNAME) {
        handleInputChange(setRepresentativeLastName, value);
      }
    }

    setShareholdersDirectors((prev) => {
      const updatedShareholdersDirectors: ShareholderDirector[] = [...prev];
      updatedShareholdersDirectors[index] = {
        ...updatedShareholdersDirectors[index],
        [field]: value,
        [ShareholderDirectorField.ROLE]: CompanyRole.DIRECTOR,
      };
      if (shouldMarkDirty) {
        setIsDirty(true);
        onDirtyChange(true);
      }
      return updatedShareholdersDirectors;
    });
  };

  const directorEmails = extractDirectorEmails(shareholdersDirectors);
  const allDirectorsEmailPresent = directorEmails.length > 0;
  useEffect(() => {
    const initializeDraft = async () => {
      if (!isEditable) {
        setIsDirty(false);
        onDirtyChange(false);
      }
      if (initPage) {
        try {
          setShouldMarkDirty(false);
          const data = await fetchDraft(parseInt(userId));
          if (data) {
            setInitialDraftData(data);
            let authChecked = data.authChecked || false;
            let directorChecked = data.directorChecked || false;
            let repFirstName = data.repFirstName || "";
            let repLastName = data.repLastName || "";
            let repEmail = data.repEmail || "";
            let repTitle = data.repTitle || "";
            let merchantFirstName = data.userFirstName || "";
            let merchantLastName = data.userLastName || "";
            let merchantTitle = data.merchantTitle || "";
            let shareholdersDirectors = data.shareHoldersDirectors || [];
            let numOfShareholdersDirectors = shareholdersDirectors.length || 1;

            // Derive checkbox state and rep info
            const checkboxState = deriveCheckboxState(
              {
                repFirstName,
                repLastName,
                repEmail,
                repTitle,
                userFirstName: merchantFirstName,
                userLastName: merchantLastName,
                merchantTitle,
                authChecked,
                directorChecked,
                shareHoldersDirectors: shareholdersDirectors,
                numOfShareHoldersDirectors: numOfShareholdersDirectors,
              },
              userEmail
            );

            authChecked = checkboxState.authChecked;
            directorChecked = checkboxState.directorChecked;
            repFirstName = checkboxState.repFirstName;
            repLastName = checkboxState.repLastName;
            repEmail = checkboxState.repEmail;
            repTitle = checkboxState.repTitle;

            // Set initial checkbox states first
            setInitialAuthChecked(authChecked);
            setInitialDirectorChecked(directorChecked);
            setAuthChecked(authChecked);
            setDirectorChecked(directorChecked);

            // Set other states
            setRepresentativeFirstName(repFirstName);
            setRepresentativeLastName(repLastName);
            setMerchantFirstName(merchantFirstName);
            setMerchantLastName(merchantLastName);
            setRepEmail(repEmail);
            setRepresentativeTitle(repTitle);
            setMerchantTitle(merchantTitle);
            setShareholdersDirectors(shareholdersDirectors);
            setNumOfShareholdersDirectors(numOfShareholdersDirectors);

            // Update Redux
            hydrateRepDataToRedux(dispatch, {
              repFirstName,
              repLastName,
              userFirstName: merchantFirstName,
              userLastName: merchantLastName,
              repEmail,
              repTitle,
              merchantTitle,
              shareholdersDirectors,
            });

            setError(INITIAL_ERRORS);

            setIsDirty(false);
            onDirtyChange(false);
          }
        } catch (error) {
          toast.error(`Error loading draft: ${error}`);
        } finally {
          setInitPage(false);
          setShouldMarkDirty(true);
        }
      }
    };
    initializeDraft();
  }, [initPage, userId, dispatch]);

  useEffect(() => {
    if (!shouldMarkDirty) return;

    if (authChecked !== initialAuthChecked && authChecked) {
      handleInputChange(setRepresentativeLastName, merchantLastName);
      handleInputChange(setRepresentativeFirstName, merchantFirstName);
      handleInputChange(setRepresentativeTitle, merchantTitle);
      handleInputChange(setRepEmail, userEmail as string);
    } else if (
      directorChecked !== initialDirectorChecked &&
      directorChecked &&
      allDirectorsEmailPresent
    ) {
      if (!directorEmails.includes(repEmail)) {
        handleInputChange(setRepresentativeLastName, "");
        handleInputChange(setRepresentativeFirstName, "");
        handleInputChange(setRepresentativeTitle, "");
        handleInputChange(setRepEmail, "");
      }
      if (directorEmails.includes(repEmail)) {
        const directorInfo = shareholdersDirectors.find(
          (user) => user.email === repEmail
        );
        if (directorInfo) {
          handleInputChange(setRepresentativeLastName, directorInfo.lastName);
          handleInputChange(setRepresentativeFirstName, directorInfo.firstName);
          handleInputChange(setRepresentativeTitle, "Director");
          handleInputChange(setRepEmail, directorInfo.email);
        }
      }
      if (!allDirectorsEmailPresent) {
        handleInputChange(setDirectorChecked, false);
      }
    }
  }, [
    authChecked,
    directorChecked,
    initialAuthChecked,
    initialDirectorChecked,
    directorEmails,
    merchantLastName,
    merchantFirstName,
    merchantTitle,
    userEmail,
    repEmail,
    representativeLastName,
    representativeFirstName,
    representativeTitle,
    shareholdersDirectors,
    allDirectorsEmailPresent,
  ]);

  const onNumberOfShareholdersDirectorsChange = (value: number) => {
    setError((prev) => ({ ...prev, shareholderDirector: "" }));
    handleInputChange(setNumOfShareholdersDirectors, Number(value));

    if (value < numOfShareholdersDirectors && shareholdersDirectors[value]) {
      const updatedShareholdersDirectors = [...shareholdersDirectors];
      updatedShareholdersDirectors.pop();
      handleInputChange(setShareholdersDirectors, updatedShareholdersDirectors);
    }
  };

  const removeShareholderDirector = (index: number) => {
    const directorToRemove = shareholdersDirectors[index];

    // If the removed director was selected as the representative, clear rep fields
    if (directorChecked && directorToRemove?.email === repEmail) {
      handleInputChange(setRepresentativeFirstName, "");
      handleInputChange(setRepresentativeLastName, "");
      handleInputChange(setRepresentativeTitle, "");
      handleInputChange(setRepEmail, "");
      handleInputChange(setDirectorChecked, false);
    }

    const updatedShareholdersDirectors = [...shareholdersDirectors];
    updatedShareholdersDirectors.splice(index, 1);
    handleInputChange(setShareholdersDirectors, updatedShareholdersDirectors);
    handleInputChange(
      setNumOfShareholdersDirectors,
      numOfShareholdersDirectors - 1
    );
  };

  const handleAuthDirectorEmailChange = (email: string) => {
    const directorInfo = shareholdersDirectors.find(
      (user) => user.email === email
    );
    if (directorInfo) {
      handleInputChange(setRepresentativeLastName, directorInfo.lastName);
      handleInputChange(setRepresentativeFirstName, directorInfo.firstName);
      handleInputChange(setRepresentativeTitle, "Director");
      handleInputChange(setRepEmail, email);
    }
  };

  const saveDraft = (showToast: boolean = true) => {
    saveOrgDraftMutation.mutateAsync(
      {
        userId: parseInt(userId),
        token,
        repFirstName: representativeFirstName,
        repLastName: representativeLastName,
        userFirstName: merchantFirstName,
        userLastName: merchantLastName,
        repEmail,
        repTitle: representativeTitle,
        merchantTitle,
        authChecked,
        directorChecked,
        shareHoldersDirectors: shareholdersDirectors,
        numOfShareHoldersDirectors: numOfShareholdersDirectors,
      },
      {
        onSuccess: () => {
          setIsDirty(false);
          onDirtyChange(false);
          setInitialAuthChecked(authChecked);
          setInitialDirectorChecked(directorChecked);
          setInitialDraftData({
            repFirstName: representativeFirstName,
            repLastName: representativeLastName,
            userFirstName: merchantFirstName,
            userLastName: merchantLastName,
            repEmail,
            repTitle: representativeTitle,
            merchantTitle,
            authChecked,
            directorChecked,
            shareHoldersDirectors: shareholdersDirectors,
            numOfShareHoldersDirectors: numOfShareholdersDirectors,
          });
          if (showToast) {
            toast.success("Draft Saved", { autoClose: 2000 });
          }
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  const resetForm = () => {
    if (initialDraftData) {
      let authChecked = initialDraftData.authChecked || false;
      let directorChecked = initialDraftData.directorChecked || false;
      let repFirstName = initialDraftData.repFirstName || "";
      let repLastName = initialDraftData.repLastName || "";
      let repEmail = initialDraftData.repEmail || "";
      let repTitle = initialDraftData.repTitle || "";
      let merchantFirstName = initialDraftData.userFirstName || "";
      let merchantLastName = initialDraftData.userLastName || "";
      let merchantTitle = initialDraftData.merchantTitle || "";
      let shareholdersDirectors = initialDraftData.shareHoldersDirectors || [];
      let numOfShareholdersDirectors = shareholdersDirectors.length || 1;

      const directorEmails = extractDirectorEmails(shareholdersDirectors);

      authChecked = wasAuthCheckboxChecked(initialDraftData, userEmail);
      directorChecked = wasDirectorCheckboxChecked(
        initialDraftData,
        directorEmails
      );

      const checkboxState = deriveCheckboxState(
        {
          repFirstName,
          repLastName,
          repEmail,
          repTitle,
          userFirstName: merchantFirstName,
          userLastName: merchantLastName,
          merchantTitle,
          authChecked,
          directorChecked,
          shareHoldersDirectors: shareholdersDirectors,
          numOfShareHoldersDirectors: numOfShareholdersDirectors,
        },
        userEmail
      );

      authChecked = checkboxState.authChecked;
      directorChecked = checkboxState.directorChecked;
      repFirstName = checkboxState.repFirstName;
      repLastName = checkboxState.repLastName;
      repEmail = checkboxState.repEmail;
      repTitle = checkboxState.repTitle;

      setRepresentativeFirstName(repFirstName);
      setRepresentativeLastName(repLastName);
      setMerchantFirstName(merchantFirstName);
      setMerchantLastName(merchantLastName);
      setRepEmail(repEmail);
      setRepresentativeTitle(repTitle);
      setMerchantTitle(merchantTitle);
      setAuthChecked(authChecked);
      setDirectorChecked(directorChecked);
      setInitialAuthChecked(authChecked);
      setInitialDirectorChecked(directorChecked);
      setShareholdersDirectors(shareholdersDirectors);
      setNumOfShareholdersDirectors(numOfShareholdersDirectors);

      hydrateRepDataToRedux(dispatch, {
        repFirstName,
        repLastName,
        userFirstName: merchantFirstName,
        userLastName: merchantLastName,
        repEmail,
        repTitle,
        merchantTitle,
        shareholdersDirectors,
      });

      setError(INITIAL_ERRORS);

      setIsDirty(false);
      onDirtyChange(false);
    }
  };

  useEffect(() => {
    onSaveDraft(saveDraft);
    onResetForm(resetForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSaveDraft, onResetForm]);

  return (
    <div className={classes.container}>
      <AuthRepInfo />
      <div className={classes.inputBox}>
        <User
          isEditable={isEditable}
          authChecked={authChecked}
          setAuthChecked={(value) => {
            handleInputChange(setAuthChecked, value);
          }}
          error={error}
          setMerchantFirstName={(value) => {
            handleInputChange(setMerchantFirstName, value);
            setError((prev) => ({ ...prev, userFirstName: "" }));
          }}
          merchantFirstName={merchantFirstName}
          setMerchantLastName={(value) => {
            handleInputChange(setMerchantLastName, value);
            setError((prev) => ({ ...prev, userLastName: "" }));
          }}
          merchantLastName={merchantLastName}
          setMerchantTitle={(value) => {
            handleInputChange(setMerchantTitle, value);
            setError((prev) => ({ ...prev, merchantTitle: "" }));
          }}
          merchantTitle={merchantTitle}
        />
        <img src={Line} alt="line" style={{ width: "190%" }} />
        <ShareholdersDirectors
          isReadOnly={!isEditable}
          numOfShareholdersDirectors={numOfShareholdersDirectors}
          onNumberOfShareholdersDirectorsChange={
            onNumberOfShareholdersDirectorsChange
          }
          error={error.shareholderDirector}
          shareholdersDirectors={shareholdersDirectors}
          updateShareholderDirectorValue={updateShareholderDirectorValue}
          onRemoveDirector={removeShareholderDirector}
        />
        <img src={Line} alt="line" style={{ width: "190%" }} />
        {!authChecked && (
          <Auth
            isEditable={isEditable}
            numOfDirectors={numOfShareholdersDirectors}
            allDirectorsEmailPresent={allDirectorsEmailPresent}
            directorChecked={directorChecked}
            setDirectorChecked={(value) => {
              handleInputChange(setDirectorChecked, value);
            }}
            error={error}
            repEmail={repEmail}
            directorEmails={directorEmails}
            handleAuthDirectorEmailChange={handleAuthDirectorEmailChange}
            setRepEmail={(value) => {
              handleInputChange(setRepEmail, value);
              setError((prev) => ({ ...prev, repEmail: "" }));
            }}
            setRepresentativeFirstName={(value) => {
              handleInputChange(setRepresentativeFirstName, value);
              setError((prev) => ({ ...prev, representative_first_name: "" }));
            }}
            representativeFirstName={representativeFirstName}
            setRepresentativeLastName={(value) => {
              handleInputChange(setRepresentativeLastName, value);
              setError((prev) => ({ ...prev, representative_last_name: "" }));
            }}
            representativeLastName={representativeLastName}
            setRepresentativeTitle={(value) => {
              handleInputChange(setRepresentativeTitle, value);
              setError((prev) => ({ ...prev, representative_title: "" }));
            }}
            representative_title={representativeTitle}
          />
        )}
      </div>
      <div className={classes.buttonWrapper}>
        <Button
          onClick={saveDraft}
          textColor={color.WHITE}
          backgroundColor={color.LOANTEXT}
          children="Save Draft"
          width="100%"
          disabled={!isEditable}
        />
      </div>
    </div>
  );
};

export default BorrowerRep;
